export type ManageDelegatesTab = 'delegates' | 'owners';

export type DelegateStatus =
  | 'all'
  | 'active'
  | 'deleted'
  | 'rejected'
  | 'revoked'
  | 'expired'
  | 'pending';

export interface ManageDelegateRow {
  id: string;
  email: string;
  /** Display name when the list API returns first/last name. */
  name?: string;
  /** Primary / legacy single scope label. Prefer `scopes` when present. */
  scope: string;
  /** All granted scopes (Figma: first chip + “+N” overflow). */
  scopes?: string[];
  /** Role + scope pairs from GET constraints, used on Delete. */
  constraintRefs?: Array<{
    role: string;
    scope: string;
    entityId?: string[];
    entityType?: string;
  }>;
  status: Exclude<DelegateStatus, 'all'>;
  createdAt: string;
  expiryAt: string;
}

export type OwnerScopeStatus =
  | 'grant'
  | 'pending'
  | 'rejected'
  | 'revoked'
  | 'deleted';

export interface OwnerScopeState {
  label: string;
  status: OwnerScopeStatus;
}

export interface ManageOwnerRow {
  id: string;
  /** Owner's control-plane user id; sent as the `did` header. */
  delegatorId?: string;
  email: string;
  /** Owner display name when known; otherwise derived from email. */
  name?: string;
  /** Organisation the owner belongs to. */
  organisationName?: string;
  /** Role of the owner who granted this delegation (Consumer, Provider, …). */
  ownerRole?: string;
  scope: string;
  scopes?: string[];
  /** Per-scope grant state. Defaults to that role’s catalog when omitted. */
  scopeStates?: OwnerScopeState[];
  status: Exclude<DelegateStatus, 'all'>;
  createdAt: string;
  expiryAt: string;
}

export interface AddDelegateForm {
  email: string;
  scope: string;
  expiryAt: string;
  comments: string;
  /** Control-plane user id (`result.sub`) from by-identifier. */
  userSub?: string;
}

export interface ManageDelegatesFilters {
  dateRange: ManageDelegatesDateRange;
  statuses: Exclude<DelegateStatus, 'all'>[];
}

export interface ManageDelegatesDateRange {
  startDate: string | null;
  endDate: string | null;
}

export interface AddDelegateFormErrors {
  email?: string;
  scope?: string;
  expiryAt?: string;
  comments?: string;
  submit?: string;
}

export interface RequestOwnerPayload {
  owner: ManageOwnerRow;
  /** Newly requested scopes. */
  scopes: string[];
  requestScopes: string[];
  revokeScopes: string[];
}

export interface DelegateScopeOption {
  label: string;
  value: string;
  /** Shown under the label in the Choose Access scope list. */
  description?: string;
  /** When true, the option cannot be selected. */
  disabled?: boolean;
  /** Optional chip next to the label (e.g. Coming soon). */
  badge?: string;
  /** Role section heading in the Add Delegate dropdown. */
  group?: string;
}

/** Copy for each access scope, keyed by {@link normalizeScopeKey}. */
export const DELEGATE_SCOPE_DESCRIPTIONS: Record<string, string> = {
  'data access':
    'Unlocks Asset Management in the sidebar. The delegate can access all the assets you already have access to.',
  'asset management':
    'Unlocks the Asset menu in the sidebar — Manage Platform Assets, Asset File Upload, Access Request, and Manage Feedback.',
  'own asset management':
    'Unlocks Add Dataset, Add Model, Assets, Access Policy, and My Asset Requests (Asset Request tab) under Manage Assets.',
  'user management':
    'Unlocks Platform Users under Manage Platform in the sidebar.',
  'org user management':
    'Unlocks Manage Users in the sidebar — user requests, org users, and user activities.',
  'org asset management':
    'Unlocks Add Dataset, Add Model, Assets, Access Policy, and My Asset Requests (Asset Request tab) under Manage Assets.',
  'org asset publish':
    'Unlocks the Publish Assets tab under My Asset Requests in the sidebar.',
  'organisation management':
    'Unlocks Manage Organisations and Organisation Requests under Manage Platform in the sidebar.',
  'org management':
    'Unlocks Manage Organisations and Organisation Requests under Manage Platform in the sidebar.',
};

export function descriptionForScope(scope: string): string {
  return DELEGATE_SCOPE_DESCRIPTIONS[normalizeScopeKey(scope)] ?? '';
}

export interface RequestScopeItem {
  label: string;
  granted: boolean;
}

export const DELEGATE_STATUS_FILTER_OPTIONS: Array<{
  value: DelegateStatus;
  label: string;
}> = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'deleted', label: 'Deleted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'revoked', label: 'Revoked' },
  { value: 'expired', label: 'Expired' },
];

const CONSUMER_SCOPE_LABELS = ['Data-access'];
const PROVIDER_SCOPE_LABELS = ['own-asset-management', 'Data-access'];
const ORG_ADMIN_SCOPE_LABELS = [
  'Data-access',
  'org-user-management',
  'org-asset-management',
  'org-asset-publish',
];
const PLATFORM_ADMIN_SCOPE_LABELS = [
  'asset-management',
  'user-management',
  'org-management',
];

/** Scopes a role can grant or request (Scope View). */
export const DELEGATE_SCOPES_BY_ROLE: Record<string, string[]> = {
  consumer: [...CONSUMER_SCOPE_LABELS],
  provider: [...PROVIDER_SCOPE_LABELS],
  'organisation admin': [...ORG_ADMIN_SCOPE_LABELS],
  'platform admin': [...PLATFORM_ADMIN_SCOPE_LABELS],
};

function scopeApiValue(label: string): string {
  const key = normalizeScopeKey(label);
  if (key === 'own asset management') {
    return 'own-asset-management';
  }
  if (key === 'org asset management') {
    return 'org-asset-management';
  }
  if (key === 'org management') {
    return 'org-management';
  }
  return label.toLowerCase().replace(/[\s_]+/g, '-');
}

export function scopeDisplayLabel(label: string): string {
  const key = normalizeScopeKey(label);
  if (
    key === 'asset management' ||
    key === 'own asset management' ||
    key === 'org asset management'
  ) {
    return 'Asset Management';
  }
  if (key === 'data access') {
    return 'Data Access';
  }
  if (key === 'user management' || key === 'org user management') {
    return 'User Management';
  }
  if (key === 'org asset publish') {
    return 'Asset Publish';
  }
  if (key === 'org management' || key === 'organisation management') {
    return 'Organisation Management';
  }
  return label;
}

/**
 * Role sent on POST /auth/delegation for a chosen scope.
 * Org admin and platform admin may grant lower-role scopes; those still map to
 * `consumer` / `provider` / `org_admin` so `roles[]` stays valid.
 */
export function delegationApiRoleForScope(
  delegatorRole: string,
  scope: string,
): string {
  const scopeKey = normalizeScopeKey(scope);
  if (scopeKey === 'data access') {
    return 'consumer';
  }
  if (scopeKey === 'own asset management') {
    return 'provider';
  }
  if (scopeKey.startsWith('org ') && scopeKey !== 'org management') {
    return 'org_admin';
  }
  return delegatorRole;
}

/**
 * Scopes sent with a selected option but not shown as their own checkbox.
 * Org-admin Asset Management → `own-asset-management` (provider role).
 * Platform-admin Asset Management → `asset-publish` (same delegator role).
 */
const BUNDLED_DELEGATION_SCOPES: Record<string, string> = {
  'org asset management': 'own-asset-management',
  'asset management': 'asset-publish',
};

export function withBundledDelegationScopes(scopes: string[]): string[] {
  const result: string[] = [];
  const seen = new Set<string>();
  const push = (scope: string) => {
    const key = normalizeScopeKey(scope);
    if (!key || seen.has(key)) return;
    seen.add(key);
    result.push(scope);
  };
  for (const scope of scopes) {
    push(scope);
    const extra = BUNDLED_DELEGATION_SCOPES[normalizeScopeKey(scope)];
    if (extra) push(extra);
  }
  return result;
}

/**
 * Companion scopes are hidden when the parent is the selectable option.
 * Provider `own-asset-management` stays visible because it is itself an option.
 */
export function isBundledCompanionScope(
  scope: string,
  catalog: string[],
): boolean {
  const key = normalizeScopeKey(scope);
  const catalogKeys = new Set(catalog.map((item) => normalizeScopeKey(item)));
  if (
    key === 'own asset management' &&
    catalogKeys.has('org asset management') &&
    !catalogKeys.has('own asset management')
  ) {
    return true;
  }
  return (
    key === 'asset publish' &&
    catalogKeys.has('asset management') &&
    !catalogKeys.has('asset publish')
  );
}

/** One `roles[]` entry per API role — required when mixing e.g. data-access + own-asset-management. */
export function groupScopesByDelegationRole(
  delegatorRole: string,
  scopes: string[],
): Array<{ role: string; scopes: string[] }> {
  const grouped = new Map<string, string[]>();
  for (const scope of withBundledDelegationScopes(scopes)) {
    const role = delegationApiRoleForScope(delegatorRole, scope);
    const list = grouped.get(role) ?? [];
    list.push(scope);
    grouped.set(role, list);
  }
  return [...grouped.entries()].map(([role, items]) => ({
    role,
    scopes: items,
  }));
}

function scopeOptionsFromLabels(labels: string[]): DelegateScopeOption[] {
  return labels.map((label) => {
    const value = scopeApiValue(label);
    return {
      label: scopeDisplayLabel(label),
      value,
      description: descriptionForScope(value) || descriptionForScope(label),
    };
  });
}

const CONSUMER_ROLE_SCOPE_OPTIONS: DelegateScopeOption[] =
  scopeOptionsFromLabels(CONSUMER_SCOPE_LABELS);

/** Consumer can only grant / hold Data-access. */
export const CONSUMER_DELEGATE_SCOPE_OPTIONS: DelegateScopeOption[] =
  CONSUMER_ROLE_SCOPE_OPTIONS.map((opt) =>
    opt.value === 'data-access'
      ? {
          ...opt,
          description:
            'Unlocks Asset Details in the sidebar. The delegate can access all the assets you already have access to.',
        }
      : opt,
  );

/** Provider can grant own-asset-management and Data-access. */
export const PROVIDER_DELEGATE_SCOPE_OPTIONS: DelegateScopeOption[] =
  scopeOptionsFromLabels(PROVIDER_SCOPE_LABELS);

/** Organisation Admin grants only the four scopes that unlock the Org Admin sidebar. */
export const ORG_ADMIN_DELEGATE_SCOPE_OPTIONS: DelegateScopeOption[] =
  scopeOptionsFromLabels(ORG_ADMIN_SCOPE_LABELS);

/**
 * Platform Admin onboarding only offers scopes that unlock the Admin Delegate
 * sidebar. Consumer, provider, and org-admin scopes stay on those roles' lists.
 */
export const PLATFORM_ADMIN_DELEGATE_SCOPE_OPTIONS: DelegateScopeOption[] =
  scopeOptionsFromLabels(PLATFORM_ADMIN_SCOPE_LABELS);

export function normalizeScopeKey(value: string): string {
  return (value || '')
    .toLowerCase()
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function isConsumerOwnerRole(ownerRole?: string): boolean {
  return normalizeScopeKey(ownerRole || '') === 'consumer';
}

export function isPlatformAdminOwnerRole(ownerRole?: string): boolean {
  const key = normalizeScopeKey(ownerRole || '');
  return key.includes('platform') || key === 'cos admin' || key === 'admin';
}

/** Friendly Owner Role labels for API enums. */
export function ownerRoleDisplayLabel(ownerRole?: string): string {
  const raw = (ownerRole || '').trim();
  if (!raw) return '';
  const key = normalizeScopeKey(raw);
  if (key === 'consumer') return 'Consumer';
  if (key === 'provider') return 'Provider';
  if (key === 'compute') return 'Compute';
  if (key === 'org admin') return 'Organisation Admin';
  if (key.includes('platform') || key === 'cos admin' || key === 'admin') {
    return 'Platform Admin';
  }
  return raw;
}

export function scopesForOwnerRole(ownerRole?: string): string[] {
  const key = normalizeScopeKey(ownerRole || '');
  if (key.includes('platform'))
    return DELEGATE_SCOPES_BY_ROLE['platform admin'];
  if (key.includes('org')) return DELEGATE_SCOPES_BY_ROLE['organisation admin'];
  if (key.includes('provider')) return DELEGATE_SCOPES_BY_ROLE['provider'];
  return DELEGATE_SCOPES_BY_ROLE['consumer'];
}

const GRANTED_SCOPE_STATUSES = new Set<string>(['grant', 'granted', 'active']);

function rowStatusToScopeStatus(
  status: Exclude<DelegateStatus, 'all'>,
): OwnerScopeStatus {
  if (status === 'pending') return 'pending';
  if (status === 'rejected') return 'rejected';
  if (status === 'revoked') return 'revoked';
  if (status === 'deleted') return 'deleted';
  if (status === 'expired') return 'revoked';
  return 'grant';
}

/** Every default scope for the owner role, with grant / pending / rejected / … */
export function ownerScopeStates(row: ManageOwnerRow): OwnerScopeState[] {
  const catalog = scopesForOwnerRole(row.ownerRole);
  const explicit = new Map(
    (row.scopeStates ?? []).map((item) => [
      normalizeScopeKey(item.label),
      item.status,
    ]),
  );
  const held = new Set(rowScopes(row).map(normalizeScopeKey));
  return catalog.map((label) => {
    const key = normalizeScopeKey(label);
    const status = explicit.get(key);
    if (status) return { label, status };
    if (held.has(key)) {
      return { label, status: rowStatusToScopeStatus(row.status) };
    }
    return { label, status: 'pending' };
  });
}

/** Currently granted scopes, limited to that owner role’s catalog. */
export function grantedOwnerScopes(row: ManageOwnerRow): string[] {
  return ownerScopeStates(row)
    .filter((item) => GRANTED_SCOPE_STATUSES.has(item.status))
    .map((item) => item.label);
}

export function rowScopes(row: { scope: string; scopes?: string[] }): string[] {
  if (row.scopes?.length) return row.scopes;
  return row.scope ? [row.scope] : [];
}

/** Derive a display name from an email local-part (e.g. anil.deshmukh → Anil Deshmukh). */
export function displayNameFromEmail(email: string): string {
  const local = (email || '').split('@')[0]?.trim();
  if (!local) return email || 'this user';
  return local
    .split(/[._\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}
